import { useSyncExternalStore } from 'react'

/**
 * Per-practice "the user closed the sidebar Capital guide card" flag. Lives in
 * `localStorage` so it survives a reload, a tab close and a new tab: the card
 * is an ad, and a clinic that shut it once should not meet it again on this
 * browser.
 *
 * Keyed on the consolidated practice only, never on the offer — a refill or
 * renewal must not resurrect a card the clinic already rejected, and switching
 * practice reads a different key.
 *
 * Deliberately not listed in `util/storage.ts`, so logging out does not clear
 * it: the dismissal is a property of the browser, not of the session.
 */
const KEY = (consolidatedPracticeId: string): string =>
	`capital_guide_dismissed_${consolidatedPracticeId}`

const EVENT = 'capital-guide-dismissed'

const dismissedInMemory = new Set<string>()

export const dismissCapitalGuide = (consolidatedPracticeId: string): void => {
	if (typeof window === 'undefined') return
	dismissedInMemory.add(consolidatedPracticeId)
	try {
		window.localStorage.setItem(KEY(consolidatedPracticeId), '1')
	} catch {
		// storage blocked — in-memory is all we get
	}
	window.dispatchEvent(new Event(EVENT))
}

const isCapitalGuideDismissed = (consolidatedPracticeId: string | undefined): boolean => {
	if (typeof window === 'undefined' || !consolidatedPracticeId) return false
	if (dismissedInMemory.has(consolidatedPracticeId)) return true
	try {
		return window.localStorage.getItem(KEY(consolidatedPracticeId)) === '1'
	} catch {
		return false
	}
}

const subscribe = (cb: () => void): (() => void) => {
	if (typeof window === 'undefined') return () => {}
	window.addEventListener(EVENT, cb)
	window.addEventListener('storage', cb)
	return () => {
		window.removeEventListener(EVENT, cb)
		window.removeEventListener('storage', cb)
	}
}

export const useCapitalGuideDismissed = (consolidatedPracticeId: string | undefined): boolean =>
	useSyncExternalStore(
		subscribe,
		() => isCapitalGuideDismissed(consolidatedPracticeId),
		() => false
	)
